import { ThemeDefinition, ThemeId } from './types';

export const DEFAULT_THEME_ID: ThemeId = 'obsidian';

export const THEMES: ThemeDefinition[] = [
  {
    id: 'obsidian',
    name: 'Obsidian Terminal',
    tagline: 'Classic Terminal Dark',
    description: 'The authoritative dark terminal interface with deep slate tones, vibrant emerald telemetry, and high-clarity metrics.',
    type: 'dark',
    isDefault: true,
    colors: {
      bgApp: '#0a0d14',
      bgSurface: '#0c101a',
      bgElevated: '#0f1420',
      bgMuted: '#151c2c',
      bgInput: '#06080d',
      borderSubtle: 'rgba(30, 41, 59, 0.5)',
      borderDefault: '#1e293b',
      borderStrong: '#334155',
      textPrimary: '#f8fafc',
      textSecondary: '#cbd5e1',
      textMuted: '#64748b',
      accentPrimary: '#10b981',
      accentSecondary: '#06b6d4',
      accentMuted: 'rgba(16, 185, 129, 0.15)',
      focusRing: '#10b981',
      headerBg: 'rgba(10, 13, 20, 0.9)',
      sidebarBg: '#0c101a',
      footerBg: '#080b11',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight Blue',
    tagline: 'Deep Navy & Cyan Desk',
    description: 'High-contrast institutional trading desk palette with deep marine sapphire surfaces and luminous sky-blue accents.',
    type: 'dark',
    colors: {
      bgApp: '#070d1e',
      bgSurface: '#0c152e',
      bgElevated: '#0f1b3b',
      bgMuted: '#14234c',
      bgInput: '#050914',
      borderSubtle: 'rgba(34, 56, 110, 0.5)',
      borderDefault: '#1a2b56',
      borderStrong: '#2a4382',
      textPrimary: '#f0f6fc',
      textSecondary: '#93c5fd',
      textMuted: '#60a5fa',
      accentPrimary: '#38bdf8',
      accentSecondary: '#0ea5e9',
      accentMuted: 'rgba(56, 189, 248, 0.15)',
      focusRing: '#38bdf8',
      headerBg: 'rgba(7, 13, 30, 0.9)',
      sidebarBg: '#0c152e',
      footerBg: '#050914',
    },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    tagline: 'Monochrome Pro Studio',
    description: 'Ultra-clean charcoal and graphite theme with neutral surfaces, focused monochrome telemetry, and distraction-free contrast.',
    type: 'dark',
    colors: {
      bgApp: '#111215',
      bgSurface: '#18191e',
      bgElevated: '#1f2026',
      bgMuted: '#262830',
      bgInput: '#0e0f12',
      borderSubtle: 'rgba(46, 49, 59, 0.5)',
      borderDefault: '#2e313b',
      borderStrong: '#454958',
      textPrimary: '#f4f4f5',
      textSecondary: '#d4d4d8',
      textMuted: '#a1a1aa',
      accentPrimary: '#e2e8f0',
      accentSecondary: '#38bdf8',
      accentMuted: 'rgba(226, 232, 240, 0.12)',
      focusRing: '#cbd5e1',
      headerBg: 'rgba(17, 18, 21, 0.9)',
      sidebarBg: '#18191e',
      footerBg: '#0e0f12',
    },
  },
  {
    id: 'emerald',
    name: 'Emerald Market',
    tagline: 'Forest Matrix Telemetry',
    description: 'Rich dark emerald backdrop engineered for algorithmic monitoring with deep forest tones and bright teal/mint highlights.',
    type: 'dark',
    colors: {
      bgApp: '#06120e',
      bgSurface: '#091914',
      bgElevated: '#0d241c',
      bgMuted: '#112e24',
      bgInput: '#040d0a',
      borderSubtle: 'rgba(20, 61, 48, 0.5)',
      borderDefault: '#143d30',
      borderStrong: '#1e5a47',
      textPrimary: '#ecfdf5',
      textSecondary: '#a7f3d0',
      textMuted: '#6ee7b7',
      accentPrimary: '#10b981',
      accentSecondary: '#14b8a6',
      accentMuted: 'rgba(16, 185, 129, 0.18)',
      focusRing: '#10b981',
      headerBg: 'rgba(6, 18, 14, 0.9)',
      sidebarBg: '#091914',
      footerBg: '#040d0a',
    },
  },
  {
    id: 'arctic',
    name: 'Arctic Light',
    tagline: 'Clean Daylight Terminal',
    description: 'Crisp, high-contrast light theme with white and cool slate surfaces, dark legible typography, and vivid cobalt accents.',
    type: 'light',
    colors: {
      bgApp: '#f8fafc',
      bgSurface: '#ffffff',
      bgElevated: '#f1f5f9',
      bgMuted: '#e2e8f0',
      bgInput: '#ffffff',
      borderSubtle: 'rgba(203, 213, 225, 0.8)',
      borderDefault: '#cbd5e1',
      borderStrong: '#94a3b8',
      textPrimary: '#0f172a',
      textSecondary: '#334155',
      textMuted: '#64748b',
      accentPrimary: '#2563eb',
      accentSecondary: '#0284c7',
      accentMuted: 'rgba(37, 99, 235, 0.12)',
      focusRing: '#2563eb',
      headerBg: 'rgba(248, 250, 252, 0.92)',
      sidebarBg: '#ffffff',
      footerBg: '#f1f5f9',
    },
  },
  {
    id: 'amber',
    name: 'Amber Terminal',
    tagline: 'Warm CRT Phosphor',
    description: 'Warm obsidian and amber palette evoking classic CRT financial screens with gold and solarized orange indicators.',
    type: 'dark',
    colors: {
      bgApp: '#120e0a',
      bgSurface: '#1a140d',
      bgElevated: '#221b12',
      bgMuted: '#2b2216',
      bgInput: '#0d0a07',
      borderSubtle: 'rgba(61, 47, 31, 0.5)',
      borderDefault: '#3d2f1f',
      borderStrong: '#5a452e',
      textPrimary: '#fef3c7',
      textSecondary: '#fde68a',
      textMuted: '#d97706',
      accentPrimary: '#f59e0b',
      accentSecondary: '#f97316',
      accentMuted: 'rgba(245, 158, 11, 0.18)',
      focusRing: '#f59e0b',
      headerBg: 'rgba(18, 14, 10, 0.9)',
      sidebarBg: '#1a140d',
      footerBg: '#0d0a07',
    },
  },
];

export const THEME_MAP: Record<ThemeId, ThemeDefinition> = THEMES.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<ThemeId, ThemeDefinition>
);
